import { EnumMyleLoginPermission } from "@/Common/Models/EnumMyleLoginPermission";
import { PatientExternalIdentifier } from "@/Common/Models/Patient/PatientExternalIdentifier";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import { Person } from "@/Common/Models/Person/Person";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";

interface IPatientBannerDropdownProps {
    Model: IPatientBannerModel;
    shouldHideMemo?: boolean;
    shouldHideChangePicture?: boolean;
    shouldHideActivationLabel?: boolean;
    shouldHideUpdateInformation?: boolean;
    onNewExternalCommunicationClick: () => void;
    onTaskOrMessageClick: (patient: Person, isTask: boolean) => void;
    onDropDownMenuClose: () => void;
}

class PatientBannerDropdown extends React.Component<IPatientBannerDropdownProps, never> {
    public componentDidMount(): void {
        overlayStore.showOverlay("PatientBannerDropdown", {
            transparent: true,
            onClicked: () => {
                this.props.onDropDownMenuClose();
            }
        });
    }

    public componentWillUnmount(): void {
        overlayStore.hideOverlay("PatientBannerDropdown");
    }

    public render(): JSX.Element {
        return (
            <div className="dropdown-container">
                <ul>
                    {!this.props.shouldHideMemo && (
                        <li><a onClick={e => this.onMemoClick(e)}>{cacheStore.GetString("MemoLabel")}</a></li>
                    )}
                    {!this.props.shouldHideChangePicture && (
                        <li>
                            <a onClick={e => this.onChangePictureClick(e)}>{cacheStore.GetString("ChangePictureLabel")}</a>
                        </li>
                    )}
                    <li><a onClick={e => this.onTaskOrMessageClick(e, false)}>{cacheStore.GetString("NewMessageLabel")}</a></li>
                    <li><a onClick={e => this.onTaskOrMessageClick(e, true)}>{cacheStore.GetString("NewTaskLabel")}</a></li>
                    <li><a onClick={e => this.onNewCallbackLabel(e)}>{cacheStore.GetString("NewRecallLabel")}</a></li>
                    {cacheStore.ClinicSettings.EnableExternalCommunication &&
                        <li><a onClick={e => this.onNewExternalMessage(e)}>{cacheStore.GetString("NewExternalCommunicationLabel")}</a></li>
                    }
                    {cacheStore.ClinicSettings.EnableMarkTestPatient && (
                        <li>
                            <a href="#" onClick={e => this.onMarkTestPatient(e)}>
                                {cacheStore.GetString(!this.props.Model.get("Model").PatientResponse.IsTest ?
                                    "MarkTestPatientLabel" : "UnMarkTestPatientLabel"
                                )}
                            </a>
                        </li>
                    )}
                    {!this.props.shouldHideActivationLabel && this.props.Model.get("Model").InactivableResponse && (
                        <li>
                            <a href="#" onClick={e => this.onDeactivatePatient(e)}>
                                {cacheStore.GetString(
                                    this.props.Model.get("Model").InactivableResponse.IsInactive ?
                                        "ReActivatePatientLabel" : "DeactivatePatientLabel"
                                )}
                            </a>
                        </li>
                    )}
                    {!this.props.shouldHideUpdateInformation && cacheStore.ClinicSettings.EnableExternalPatientSearch && (
                        <li>
                            <a onClick={e => this.onUpdateInformation(e)}>
                                {cacheStore.GetString("UpdateInformationLabel")}
                            </a>
                        </li>
                    )}
                    {cacheStore.ClinicSettings.EnableUserPatientAutoMergeRequest && sessionStore.hasPermission(EnumMyleLoginPermission.Administration_PatientMergeRequestPermission) && !this.props.Model.get("IsPartOfMergeRequest") && (
                        <li>
                            <a
                                onClick={() => sessionStore.dispatch(
                                    LocalPatientOperations.UI.TogglePatientMergeRequestPopup,
                                    { showPopup: true, contactId: this.props.Model.get("Model").IndividualResponse.Contact_id }
                                )}
                            >
                                {cacheStore.GetString("MergePatientLabel")}
                            </a>
                        </li>
                    )}
                    {cacheStore.ClinicSettings.EnableUserPatientAutoMergeRequest && sessionStore.hasPermission(EnumMyleLoginPermission.Administration_PatientMergeRequestPermission) && this.props.Model.get("IsPartOfMergeRequest") && (
                        <li>
                            <a
                                onClick={() => sessionStore.dispatch(
                                    LocalPatientOperations.UI.ToggleCancelPatientMergeRequestPopup,
                                    { showPopup: true, contactId: this.props.Model.get("Model").IndividualResponse.Contact_id }
                                )}
                            >
                                {cacheStore.GetString("CancelPatientMergeRequestLabel")}
                            </a>
                        </li>
                    )}
                </ul>
            </div>
        );
    }

    private onMemoClick(e: React.MouseEvent): void {
        e.preventDefault();
        sessionStore.dispatch(LocalPatientOperations.Profile.AddMemoClick);
    }

    private onChangePictureClick(e: React.MouseEvent): void {
        e.preventDefault();
        sessionStore.dispatch(LocalPatientOperations.Profile.ImageUpload_Open);
    }

    private onTaskOrMessageClick(e: React.MouseEvent, isTask: boolean): void {
        e.preventDefault();
        e.stopPropagation();
        this.props.onTaskOrMessageClick(this.getPersonFromPatientModel(), isTask);
        this.closeDropdownMenu();
    }

    private onNewCallbackLabel(e: React.MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();
        sessionStore.dispatch(LocalOperations.SendRecallPopup.Show_SendRecall_Popup, {
            patient: this.getPersonFromPatientModel()
        });
        this.closeDropdownMenu();
    }

    private onNewExternalMessage(e: React.MouseEvent): void {
        e.preventDefault();
        this.props.onNewExternalCommunicationClick();
        this.closeDropdownMenu();
    }

    private onMarkTestPatient(e: React.MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();
        sessionStore.dispatch(LocalPatientOperations.Profile.MarkTestPatientClick);
    }

    private onDeactivatePatient(e: React.MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();
        sessionStore.dispatch(LocalPatientOperations.Profile.DeactivatePatientClick);
    }

    private onUpdateInformation(e: React.MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();

        let patientModel = this.props.Model.get("Model");

        sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.DisplayExternalSearch, {
            identifier: PatientExternalIdentifier.GetExternalSearchDefaultSearchForActiveSite(patientModel),
            patientContactId: patientModel.IndividualResponse.Contact_id,
            callback: () => patientStore.ActionsProfile.loadPatient(patientModel.IndividualResponse.Contact_id)
        });

        this.closeDropdownMenu();
    }

    private closeDropdownMenu(): void {
        this.props.onDropDownMenuClose();
    }

    private getPersonFromPatientModel(): Person {
        const patientModel = this.props.Model.get("Model");
        return Person.FromPatient(patientModel);
    }
}

export { PatientBannerDropdown };
